import { type DefaultMessagesResponse } from '@post-office/message-maker-browser/types';

type MessageBody = DefaultMessagesResponse['messages'][number];

/**
 * Eligibility check for a single message template.
 * - `boolean`: static eligibility
 * - `(message) => boolean`: evaluated at filter time with the full message
 */
export type MessageEligibilityCheck = boolean | ((message: MessageBody) => boolean);

/**
 * Map of message template IDs to eligibility checks.
 * Used by `filterByClientEligibility` to drop ineligible messages
 * before experiment evaluation, preventing spurious exposures.
 */
export type MessageEligibility = Record<string, MessageEligibilityCheck>;

export type AppProps = {
	productSource: string;
	/**
	 * Optional map of message template IDs to client-side eligibility.
	 * When provided, messages whose template ID maps to `false` are
	 * filtered out before priority selection, preventing them from
	 * consuming the placement slot. Messages not in the map default
	 * to eligible.
	 *
	 * Values can be:
	 * - `boolean`: static eligibility (true = eligible, false = ineligible)
	 * - `(message) => boolean`: dynamic eligibility evaluated per message,
	 *   useful when eligibility depends on data in the message itself
	 *   (e.g. server-provided context fields). The callback receives the
	 *   full message object including any flattened `messageTemplateContext`.
	 */
	messageEligibility?: MessageEligibility;
	/**
	 * Allow message-specific props to pass through the placement without
	 * the placement needing to know about them. The placement is a dumb
	 * pipe — individual messages define and consume whatever extra props
	 * they need via their own types.
	 */
	[key: string]: unknown;
};

export type PlacementRenderProps = AppProps & {
	/** Called by message template to dismiss itself from the UI */
	dismiss: () => void;
};

// from platform/packages/search/search-page/src/common/constants/schemas/3p-config.ts
export const ConnectorSources = {
	Federated: 'federated',
	Smartlinks: 'smartlinks', // smart-link based "light" connector
	Lite: 'lite-connector',
	Full: 'full-connector', // admin-configured 3P search connector
	Backfill: 'backfill',
} as const;

export type ConnectorSource = (typeof ConnectorSources)[keyof typeof ConnectorSources];

// from platform/packages/search/search-page/src/common/utils/events/oauth-events.ts
export const OAuthSource = {
	quickFindFilter: 'quick-find-filter',
	quickFindOAuthBanner: 'quick-find-oauth-banner',
	quickFindBulkConnect: 'quick-find-bulk-connect',
	searchPageFilter: 'search-page-filter',
	searchPageOAuthBanner: 'search-page-oauth-banner',
	searchPageBulkConnect: 'search-page-bulk-connect',
	sainThirdPartyBanner: 'sain-third-party-banner',
} as const;

export type OAuthSource = (typeof OAuthSource)[keyof typeof OAuthSource];

// from platform/packages/search/search-page/src/common/utils/post-office-placement/search-page-results-inline-placement/index.tsx
interface OAuthHandlersProps {
	product: string; // typing as a string although it is coming in as ProductKey, unnecessary to copy over entire type
	connectorSource: ConnectorSource;
	successFlagTitle: React.ReactNode;
	failFlagTitle: React.ReactNode;
	outboundAuthUrl: string;
	onCompletedAction?: () => void;
	onDismissAction?: () => void;
	isOnSearchDialog?: boolean;
	extraAttributes?: Record<string, unknown>;
}

interface OauthHandlerFunctions {
	onAuthorize: (oAuthSource: OAuthSource) => void;
	onDismiss: () => void;
}

export type useOAuthHandlers = (props: OAuthHandlersProps) => OauthHandlerFunctions;

export type ConnectorProps = {
	connectorId: string;
	// index of the connector in the static list
	connectorIndex: number;
	connectorMode: ConnectorSource;
	connectorName: string;
	connectorProductKey: string; // typing as a string although it is coming in as ProductKey, unnecessary to copy over entire type
	oAuthUrl: string;
	userNeedsOAuth: boolean;
};
